// Visit options for the settle sheet's copay declaration (GDN-2405).
//
// Wraps the existing encounters list endpoint (the same
// `useListEncountersQuery` the encounters feature exposes) scoped to the
// patient, newest first. Degrades gracefully: while the list is loading —
// or if it errors / doesn't include the drawer's context encounter — the
// context encounter still appears as a bare "This visit" option so the
// declaration is never blocked on the list fetch.

import { useCallback, useMemo, useState } from 'react';
// Direct file import (no feature barrel) — avoids the encounters feature's
// auto-registration chain; the RTK endpoints are injected into baseApi.
import {
  useLazyListEncountersQuery,
  useListEncountersQuery,
} from '@features/encounters/store/api';
import type { EncounterListResponse } from '@store/generated/encounters';

type ListedEncounter = EncounterListResponse['items'][number];

export interface CopayVisitOption {
  id: string;
  /** Resolved service date (YYYY-MM-DD…), when known. */
  serviceDate: string | null;
  /** Encounter or appointment start time, when known. */
  startsAt?: string | null;
  /** Human-readable visit label, e.g. "Therapy Session". */
  label: string;
  /** First CPT code on the encounter, when coded. */
  cptCode: string | null;
  /** True for the drawer's context encounter (preselected). */
  isContext: boolean;
}

interface UseRecentEncounterOptionsArgs {
  patientId: string;
  /** The drawer's in-scope encounter — always listed first when present. */
  contextEncounterId?: string | null;
  /** Eligibility-pinned appointment time for labeling a missing context encounter. */
  contextVisitStartsAt?: string | null;
  /** Skip the list fetch entirely (e.g. while the copay mode is hidden). */
  skip?: boolean;
}

interface UseRecentEncounterOptions {
  visits: CopayVisitOption[];
  isLoading: boolean;
  /** The patient has encounters beyond the ones listed (gdn-4177). */
  hasOlder: boolean;
  /** List more of them. Callers that never call it keep the short list. */
  showOlder: () => void;
  /** A longer list is on its way; the listed visits stay put meanwhile. */
  isLoadingOlder: boolean;
}

const RECENT_VISIT_COUNT = 5;
// Each "show older" asks for this many more, up to the list endpoint's
// page cap (per_page le=100). Growing one page rather than paging keeps
// every listed visit selectable at once.
const OLDER_VISIT_STEP = 10;
const PAGE_CAP = 100;

/** How far one patient's list has grown. */
interface Paging {
  patientId: string;
  visitCount: number;
  olderPages: ListedEncounter[];
}

const NO_OLDER_PAGES: ListedEncounter[] = [];
const freshPaging = (patientId: string): Paging => ({
  patientId,
  visitCount: RECENT_VISIT_COUNT,
  olderPages: NO_OLDER_PAGES,
});

export function useRecentEncounterOptions({
  patientId,
  contextEncounterId,
  contextVisitStartsAt,
  skip,
}: UseRecentEncounterOptionsArgs): UseRecentEncounterOptions {
  // Five recent visits cover the common case, but a charge entered weeks
  // late belongs to a visit that has scrolled out of them — Island Life's
  // "Patient charge: DOS 08/21/2026" was entered on 9/25. A short list
  // must not decide which visits can take a charge, so it can grow, and
  // with no ceiling: past the endpoint's page cap it grows a whole page at
  // a time, appended below, so every encounter stays reachable.
  //
  // All of it belongs to one patient. A sheet can stay mounted while its
  // patient changes (the ledger drawer reuses one instance), and another
  // patient's encounter in this picker would attach a charge to the wrong
  // patient's visit. So growth is stored with its patient and ignored for
  // any other, and a page that arrives late for a previous patient is
  // dropped rather than appended.
  const [storedPaging, setPaging] = useState<Paging>(() => freshPaging(patientId));
  const { visitCount, olderPages } =
    storedPaging.patientId === patientId ? storedPaging : freshPaging(patientId);
  const [loadingOlderFor, setLoadingOlderFor] = useState<string | null>(null);
  const [loadOlderPage] = useLazyListEncountersQuery();

  const { data, currentData, isLoading, isFetching } = useListEncountersQuery(
    {
      patientId,
      // The canonical resolved visit date. 'start_time' used to be sent
      // here and still works, but only because the server aliases it —
      // 16.9% of encounters have no start_time and Postgres DESC is NULLS
      // FIRST, so the raw column filled this five-slot picker with
      // dateless encounters and never reached the patient's real visits
      // (gdn-3102). Asking for what we actually mean is what lets the
      // alias be retired (gdn-3104).
      sortBy: 'service_date',
      sortOrder: 'desc',
      perPage: visitCount,
    },
    {
      skip: !!skip || !patientId,
      // The encounter a charge is for is often minutes old when the list
      // opens: marking a no-show creates one, and so does Start Recording
      // on an appointment. A list served from the cache would not offer it.
      refetchOnMountOrArgChange: true,
    },
  );

  // `data` is RTK Query's last result for ANY args. For the same patient
  // that keeps the shorter list on screen while a longer one loads, so
  // nothing flickers out from under the biller. After a patient change it
  // is the previous patient's list, so it is trusted only when its
  // encounters are this patient's; until the new list lands, nothing is.
  const ownsData =
    !!data &&
    data.items.length > 0 &&
    data.items.every(encounter => !encounter.patient_id || encounter.patient_id === patientId);
  const firstPage = ownsData ? data : currentData;
  const isSwitchingPatient = isFetching && !firstPage;

  const showOlder = useCallback(() => {
    if (visitCount < PAGE_CAP) {
      setPaging(stored => {
        const base = stored.patientId === patientId ? stored : freshPaging(patientId);
        return { ...base, visitCount: Math.min(base.visitCount + OLDER_VISIT_STEP, PAGE_CAP) };
      });
      return;
    }
    // The first page now holds the newest PAGE_CAP; pages after it are
    // whole until the last, and nothing is older than a partial page.
    const requestedFor = patientId;
    const nextPage = 2 + Math.floor(olderPages.length / PAGE_CAP);
    setLoadingOlderFor(requestedFor);
    loadOlderPage(
      {
        patientId: requestedFor,
        sortBy: 'service_date',
        sortOrder: 'desc',
        perPage: PAGE_CAP,
        page: nextPage,
      },
      true,
    )
      .unwrap()
      .then(result =>
        setPaging(stored =>
          stored.patientId === requestedFor
            ? { ...stored, olderPages: [...stored.olderPages, ...result.items] }
            : stored,
        ),
      )
      // A page that fails to load leaves the list as it was, and "show
      // older" stays offered for another try.
      .catch(() => undefined)
      .finally(() =>
        setLoadingOlderFor(current => (current === requestedFor ? null : current)),
      );
  }, [visitCount, olderPages.length, loadOlderPage, patientId]);

  const listed = useMemo(() => {
    const byId = new Map<string, ListedEncounter>();
    // Keyed by id: a visit created between two page loads shifts the pages
    // by one, and must not list twice.
    for (const encounter of [...(firstPage?.items ?? []), ...olderPages]) {
      byId.set(encounter.encounter_id, encounter);
    }
    return [...byId.values()];
  }, [firstPage, olderPages]);

  const visits = useMemo(() => {
    const options: CopayVisitOption[] = listed.map(encounter => ({
      id: encounter.encounter_id,
      serviceDate: encounter.service_date ?? null,
      startsAt: encounter.start_time ?? null,
      label: humanizeEncounterType(encounter.encounter_type),
      cptCode: encounter.cpt_codes?.[0] ?? null,
      isContext: encounter.encounter_id === contextEncounterId,
    }));

    // Context encounter first — it's what the toolbar/drawer scoped to.
    const context = options.filter(option => option.isContext);
    const rest = options.filter(option => !option.isContext);
    const ordered = [...context, ...rest];

    if (contextEncounterId && context.length === 0) {
      // List still loading (or missing the context visit) — degrade to a
      // bare option so the declaration can proceed against the context.
      ordered.unshift({
        id: contextEncounterId,
        serviceDate: contextVisitStartsAt?.slice(0, 10) ?? null,
        startsAt: contextVisitStartsAt ?? null,
        label: 'Appointment',
        cptCode: null,
        isContext: true,
      });
    }

    return ordered;
  }, [contextEncounterId, contextVisitStartsAt, listed]);

  // A longer list is on its way only while fewer visits are listed than were
  // asked for. The refetch of the same list as the picker opens is not one.
  const isGrowingFirstPage =
    isFetching &&
    !isLoading &&
    !isSwitchingPatient &&
    listed.length < Math.min(visitCount, firstPage?.total ?? 0);

  return {
    visits,
    isLoading: isLoading || isSwitchingPatient,
    hasOlder: (firstPage?.total ?? 0) > listed.length,
    showOlder,
    isLoadingOlder: isGrowingFirstPage || loadingOlderFor === patientId,
  };
}

/** "therapy_session" → "Therapy session"; passthrough for humane labels. */
function humanizeEncounterType(encounterType?: string | null): string {
  const raw = encounterType?.trim();
  if (!raw) return 'Visit';
  const spaced = raw.replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ── Display helpers for CopayVisitOption ────────────────────────────────────
// Here rather than in a component file so both the copay declaration and
// the charge-attribution picker (gdn-3086) can import them without tripping
// react-refresh's only-export-components rule.

/** "Jul 6 · Therapy session · CPT 90834" — degrades to whatever we know. */
export const visitDescriptor = (visit: CopayVisitOption): string =>
  [
    visitDateTime(visit),
    visit.label,
    visit.cptCode ? `CPT ${visit.cptCode}` : null,
  ]
    .filter(Boolean)
    .join(' · ') || 'this visit';

export const visitDateTime = (visit: CopayVisitOption): string | null => {
  if (!visit.startsAt) {
    return visit.serviceDate ? shortVisitDate(visit.serviceDate) : null;
  }
  const parsed = new Date(visit.startsAt);
  if (Number.isNaN(parsed.getTime())) return visit.startsAt;
  const date = parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const time = parsed.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date}, ${time}`;
};

const shortVisitDate = (date: string): string => {
  const parsed = new Date(`${date.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
